import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useFormatMoney, moneyOrZero, RoundingStrategy } from "@faire/web--source/common/money";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { LoadingSkeleton } from "@faire/web--source/slate/LoadingSkeleton";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { ScrollingNumber } from "@faire/web--source/ui/ScrollingNumber/ScrollingNumber";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React from "react";
import styled from "styled-components";
export var InsiderSavingsSummary = function(param) {
    var title = param.title, items = param.items, total = param.total, isLoading = param.isLoading;
    var formatMoney = useFormatMoney().formatMoney;
    return /*#__PURE__*/ _jsxs(StyledColumn, {
        children: [
            /*#__PURE__*/ _jsx(Insider, {
                small: true,
                fontSize: "16px",
                color: Decorative.shade.teal
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: 12
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "subheadingSansMedium",
                children: title
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            !isLoading ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SummaryColumn, {
                        children: items === null || items === void 0 ? void 0 : items.map(function(param, index) {
                            var name = param.name, amount = param.amount, strikethrough = param.strikethrough;
                            return /*#__PURE__*/ _jsxs(SummaryRow, {
                                align: "flex-end",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "labelSansRegular",
                                        children: name
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraphSansMedium",
                                        strikethrough: strikethrough,
                                        children: amount
                                    })
                                ]
                            }, "savings-summary-item-".concat(index, "-with-amount-").concat(amount));
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "2x"
                    }),
                    /*#__PURE__*/ _jsx(HorizontalLine, {
                        stroke: "1px",
                        color: Core.border.subdued
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsxs(SummaryRow, {
                        align: "flex-start",
                        children: [
                            /*#__PURE__*/ _jsx(Column, {
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraphSansMedium",
                                    color: Decorative.shade.teal,
                                    children: total === null || total === void 0 ? void 0 : total.name
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "sectionHeaderSansMedium",
                                color: Decorative.shade.teal,
                                style: {
                                    lineHeight: "normal"
                                },
                                children: /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
                                    threshold: -50,
                                    children: /*#__PURE__*/ _jsx(ScrollingNumber, {
                                        targetNumber: formatMoney({
                                            money: moneyOrZero(total === null || total === void 0 ? void 0 : total.amount),
                                            roundingStrategy: RoundingStrategy.DROP_CENTS
                                        }),
                                        scrollDurationBasis: 0.01
                                    })
                                })
                            })
                        ]
                    })
                ]
            }) : /*#__PURE__*/ _jsx(InsiderSavingsSummarySkeleton, {})
        ]
    });
};
var skeletonMapMock = new Array(2).fill("");
var InsiderSavingsSummarySkeleton = function() {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SummaryColumn, {
                children: skeletonMapMock.map(function(_, index) {
                    return /*#__PURE__*/ _jsxs(SummaryRow, {
                        align: "flex-end",
                        children: [
                            /*#__PURE__*/ _jsx(LoadingSkeleton, {
                                width: "160px",
                                height: "20px"
                            }),
                            /*#__PURE__*/ _jsx(LoadingSkeleton, {
                                width: "26px",
                                height: "16px"
                            })
                        ]
                    }, "savings-summary-item-".concat(index, "-skeleton}"));
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(HorizontalLine, {
                stroke: "1px",
                color: Core.border.subdued
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            /*#__PURE__*/ _jsxs(SummaryRow, {
                align: "flex-start",
                children: [
                    /*#__PURE__*/ _jsx(Column, {
                        children: /*#__PURE__*/ _jsx(LoadingSkeleton, {
                            width: "160px",
                            height: "20px"
                        })
                    }),
                    /*#__PURE__*/ _jsx(LoadingSkeleton, {
                        width: "47px",
                        height: "26px"
                    })
                ]
            })
        ]
    });
};
var StyledColumn = styled(Column).withConfig({
    componentId: "sc-bcf57927-0"
})([
    "padding:",
    ";width:334px;background-color:",
    ";"
], getSpacing("5x", "6x"), Core.surface.inverse);
var SummaryColumn = styled(Column).attrs({
    gap: "1x"
}).withConfig({
    componentId: "sc-bcf57927-1"
})([
    ""
]);
var SummaryRow = styled(Row).attrs({
    justify: "space-between"
}).withConfig({
    componentId: "sc-bcf57927-2"
})([
    "width:100%;"
]);
